import { type HomeDeliveryGuidance } from 'pages/WaitingRoom/VisitCallRoom/visit-popup/components/requestedPharmacy'
import { type HomeDeliveryGuideEntrySource, type HomeDeliveryGuideSectionId } from './homeDeliveryGuideLink'

/**
 * QMD-4967: the values the Home Delivery nudge and guide events carry. Analytics segments on these
 * exact strings, so they are spelled as the ticket enumerates them and never derived from UI copy.
 */

/** `NudgeType`, per strip tone: blue is the request itself, yellow a script that went to pickup. */
export const HOME_DELIVERY_NUDGE_TYPE = {
  info: 'home_delivery_requested',
  warning: 'pickup_mismatch',
} as const satisfies Record<HomeDeliveryGuidance, string>

export type HomeDeliveryNudgeType = (typeof HOME_DELIVERY_NUDGE_TYPE)[HomeDeliveryGuidance]

/** `ButtonLabel`. Kept apart from `NudgeType` so a relabelled button doesn't silently break segmentation. */
export const HOME_DELIVERY_NUDGE_BUTTON_LABEL = {
  info: 'how_it_works',
  warning: 'how_to_change',
} as const satisfies Record<HomeDeliveryGuidance, string>

export type HomeDeliveryNudgeButtonLabel = (typeof HOME_DELIVERY_NUDGE_BUTTON_LABEL)[HomeDeliveryGuidance]

/** The guide's entry source for a link from the strip: the nudge that was showing. */
export const HOME_DELIVERY_NUDGE_ENTRY_SOURCE = HOME_DELIVERY_NUDGE_TYPE satisfies Record<
  HomeDeliveryGuidance,
  HomeDeliveryGuideEntrySource
>

/**
 * `Section` and `SectionTitle` for each guide section. The titles are the ticket's, without the
 * headings' trailing periods, so a copy edit on the page doesn't move a dashboard.
 */
export const HOME_DELIVERY_GUIDE_SECTION_ANALYTICS = {
  'how-to-prescribe': { Section: 'how_to_prescribe', SectionTitle: 'How to prescribe' },
  'how-to-change': { Section: 'how_to_change', SectionTitle: 'How to change a prescription to Home Delivery' },
  cost: { Section: 'what_it_costs', SectionTitle: 'What it costs' },
  arrival: { Section: 'when_it_arrives', SectionTitle: 'When it arrives' },
  sooner: {
    Section: 'needs_medication_sooner',
    SectionTitle: 'What if the patient needs their medication sooner?',
  },
  signatures: { Section: 'signature_required', SectionTitle: 'Are signatures required for delivery?' },
  pharmacy: { Section: 'pharmacy_fills', SectionTitle: 'What pharmacy fills the medication?' },
  address: { Section: 'different_address', SectionTitle: 'Can medication be sent to a different address?' },
  medications: {
    Section: 'moud_medications_available',
    SectionTitle: 'What MOUD medications are available for Home Delivery?',
  },
  'after-the-visit': { Section: 'after_the_visit', SectionTitle: 'What do patients do after the visit?' },
} as const satisfies Record<HomeDeliveryGuideSectionId, { Section: string; SectionTitle: string }>

export type HomeDeliveryGuideSectionAnalytics =
  (typeof HOME_DELIVERY_GUIDE_SECTION_ANALYTICS)[HomeDeliveryGuideSectionId]
